import React, { useEffect, useState } from 'react';
import './board-stats.scss';
import { BoardStatKeys, BoardStats, contractSuits, suitsFullString } from '../../_models/models';
import { IRootState } from '../../store/_root.reducer';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { renderStatRowExternal } from './board-stat-row-render';
import { suitArray } from '../../../game-engine-interface/game-engine-helper';

const COMMON_SAMPLE_LIMIT = 4;

export interface InstaBoardStatsProps extends StateProps, DispatchProps {
    my_stat?: BoardStatKeys;
    stats?: BoardStats[];
    selectedKey?: BoardStatKeys;
    isOpen?: boolean;
    onSelect?: (key: BoardStatKeys, stat: BoardStats) => void;
    onToggleOpen?: () => void;
}

const InstaBoardStats: React.FC<InstaBoardStatsProps> = ({ my_stat, stats, urlParams, selectedKey, isOpen, onSelect, onToggleOpen }) => {
    const { t } = useTranslation();

    // ---- internal fallback state if parent doesn't control everything ----
    const [internalOpen, setInternalOpen] = useState(true);
    const [internalSelectedKey, setInternalSelectedKey] = useState<BoardStatKeys | undefined>(selectedKey);

    useEffect(() => {
        if (selectedKey) {
            setInternalSelectedKey(selectedKey);
        }
    }, [selectedKey]);

    // ---- guard ----
    if (!stats) {
        return null;
    }

    const open = typeof isOpen === 'boolean' ? isOpen : internalOpen;
    const selected = selectedKey ?? internalSelectedKey;

    const handleToggle = () => {
        if (onToggleOpen) {
            onToggleOpen();
        } else {
            setInternalOpen(prev => !prev);
        }
    };
    const keyFields: (keyof BoardStatKeys)[] = ['r', 's', 'c', 'd', 'x', 'l'];
    const normalizeKey = (key?: BoardStatKeys) => keyFields.map(k => (key ? key[k] : '')).join('-');
    const keysEqual = (a?: BoardStatKeys, b?: BoardStatKeys) => {
        if (a?.uuid || b?.uuid) {
            return Boolean(a?.uuid && b?.uuid && a.uuid === b.uuid);
        }

        return normalizeKey(a) === normalizeKey(b);
    };

    const handleRowClick = (_index: number, stat: BoardStats) => {
        const key = stat.key;
        if (onSelect) {
            onSelect(key, stat);
        } else {
            setInternalSelectedKey(key);
            setInternalOpen(false);
        }
    };

    const d = [
        t('boardStats.bridgePosition.n'),
        t('boardStats.bridgePosition.e'),
        t('boardStats.bridgePosition.s'),
        t('boardStats.bridgePosition.w')
    ];

    const columnTitles = [
        t('boardStats.declarer'),
        urlParams.objective ? t('boardStats.objective') : t('boardStats.contract'),
        t('boardStats.lead'),
        t('boardStats.tricks'),
        t('boardStats.score'),
        t('boardStats.result'),
        t('boardStats.players')
    ];
    const columnClasses = ['col-declarer', 'col-contract', 'col-lead', 'col-tricks', 'col-score', 'col-result', 'col-players'];

    // Helper to compute "isMe" for any given row (used in both table and header)
    const isMeForRow = (_boardStats: BoardStats) => {
        const keyStats = keyFields
            .map(key => _boardStats.key[key])
            .sort()
            .join('-');
        const myStats = keyFields
            .map(key => (my_stat ? my_stat[key] : {}))
            .sort()
            .join('-');

        return keyStats === myStats || _boardStats.key.is_me || _boardStats.key.is_replay;
    };

    const selectedStat = selected ? stats.find(s => keysEqual(s.key, selected)) : undefined;
    const flaggedMyStats = stats.filter(stat => stat.key.is_me || stat.key.is_replay);
    const fallbackMyStat =
        flaggedMyStats.length === 0 && my_stat ? stats.find(stat => normalizeKey(stat.key) === normalizeKey(my_stat)) : undefined;
    const myStats = flaggedMyStats.length > 0 ? flaggedMyStats : fallbackMyStat ? [fallbackMyStat] : [];
    const myStatsSet = new Set(myStats);
    const myStatKeys = new Set(myStats.map(stat => normalizeKey(stat.key)));
    const fieldStats = stats.filter(stat => !myStatsSet.has(stat) && !myStatKeys.has(normalizeKey(stat.key)));
    const commonStats = [...fieldStats]
        .sort((a, b) => b.count - a.count || b.result - a.result || b.key.s - a.key.s)
        .slice(0, COMMON_SAMPLE_LIMIT);
    const commonStatsSet = new Set(commonStats);
    const otherStats = fieldStats.filter(stat => !commonStatsSet.has(stat)).sort((a, b) => b.result - a.result);
    const groupedStats = [
        { label: t('boardStats.group.myResult', 'My result'), stats: myStats },
        { label: t('boardStats.group.commonLines', 'Common field lines'), stats: commonStats },
        { label: t('boardStats.group.otherResults', 'Other results'), stats: otherStats }
    ].filter(group => group.stats.length > 0);

    const renderNativeStatRow = (stat: BoardStats, index: number) => {
        const isSelected = selected ? keysEqual(stat.key, selected) : false;
        const clickHandler = () => handleRowClick(index, stat);

        return (
            <tr
                key={`insta-board-stat-row-${stat.key.uuid ?? normalizeKey(stat.key)}-${index}`}
                className={['BoardStats__native-row', isMeForRow(stat) ? 'isMe' : '', isSelected ? 'isSelected' : '']
                    .filter(Boolean)
                    .join(' ')}
                onClick={clickHandler}
            >
                <td className="declarer">{stat.key.d >= 0 ? d[stat.key.d] : '-'}</td>
                <td className="contract">
                    <div className={stat.key.c >= 10 ? contractSuits[`${stat.key.c}`.substring(1, 2)] : 'passOut'}>
                        {`${stat.key.c}`.substring(1, 2) === '4' && (
                            <div className="notrump">{contractSuits[`${stat.key.c}`.substring(1, 2)]}</div>
                        )}
                        <div className="level">{stat.key.c > 0 ? `${stat.key.c}`.substring(0, 1) : 'Pass out'}</div>
                        <div className="x">&nbsp;{' XX'.substring(0, stat.key.x)}</div>
                    </div>
                </td>
                <td className="lead">
                    <div
                        className={
                            stat.key.l >= 0 ? suitsFullString[suitArray[stat.key.l].substring(0, 1) as keyof typeof suitsFullString] : ''
                        }
                    >
                        <span>{stat.key.l >= 0 ? suitArray[stat.key.l].substring(1) : '-'}</span>
                    </div>
                </td>
                <td className="tricks">{stat.key.c === 0 ? '-' : stat.key.r === 0 ? '=' : stat.key.r > 0 ? `+${stat.key.r}` : stat.key.r}</td>
                <td className="score">{stat.key.s}</td>
                <td className="result">{Math.round((stat.result + Number.EPSILON) * 100) / 100}</td>
                <td className="players">{stat.count}</td>
            </tr>
        );
    };

    return (
        <section className={['BoardStats', stats.length === 0 ? 'NoStats' : '', open ? 'open' : 'collapsed'].join(' ')}>
            {/* HEADER / DROPDOWN BUTTON – now contains the selection */}
            <button type="button" className="BoardStats__header" onClick={handleToggle}>
                <div className="BoardStats__header-content">
                    {open && <div className="BoardStats__header-title">{t('boardStats.travellerHeader', 'Select Sample Score')}</div>}
                    {!open && selectedStat && (
                        <div className="BoardStats__header-selection table table--preview">
                            {renderStatRowExternal(selectedStat, 0, {
                                isMe: isMeForRow(selectedStat),
                                isSelected: true,
                                disableClick: true,
                                dLabels: d
                            })}
                        </div>
                    )}
                </div>

                <span className="BoardStats__header-chevron">{open ? '▲' : '▼'}</span>
            </button>

            {/* FULL TRAVELLER TABLE WHEN OPEN */}
            {open && (
                <div className="BoardStats__panel">
                    <table className="BoardStats__native-table">
                        <colgroup>
                            {columnClasses.map(className => (
                                <col key={className} className={className} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                {columnTitles.map((title, index) => (
                                    <th key={title} className={columnClasses[index]}>
                                        {title}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        {groupedStats.map(group => (
                            <tbody key={`group-${group.label}`}>
                                <tr className="BoardStats__group-label">
                                    <td colSpan={columnTitles.length}>
                                        <span>{group.label}</span>
                                        <span>{group.stats.length}</span>
                                    </td>
                                </tr>
                                {group.stats.map(renderNativeStatRow)}
                            </tbody>
                        ))}
                    </table>
                </div>
            )}
        </section>
    );
};

const mapStateToProps = ({ game, app }: IRootState) => ({
    urlParams: app.urlParams
});

const mapDispatchToProps = {};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(InstaBoardStats);
